import { useCallback, useMemo, useState } from "react";

/**
 * 표 행 선택 상태를 소유하는 훅(데이터 레이어).
 *
 * 선택이 페이지를 가로지를 수 있으므로 표가 아니라 이 훅이 선택을 소유한다.
 * - 표 헤더 체크박스: 현재 페이지 행만 토글 (togglePage)
 * - 액션바 "전체 N건 선택": 필터된 전 데이터 토글 (selectAll / clear)
 *
 * 서버 페이징 전환 시: 전 데이터를 메모리에 들고 있지 않으므로 "전체 선택"을
 * selectAllMatching 플래그로 표현하는 확장 지점을 여기에 추가하면 된다.
 *
 * @param allRows 전체(필터 적용된) 행 배열
 * @param getRowId 행 → 고유 id (기본 row.id)
 */
export function useTableSelection(allRows, { getRowId = (row) => row.id } = {}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const isSelected = useCallback(
    (row) => selectedIds.has(getRowId(row)),
    [selectedIds, getRowId],
  );

  const toggleRow = useCallback(
    (row) => {
      const id = getRowId(row);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [getRowId],
  );

  const togglePage = useCallback(
    (pageRows) => {
      const ids = pageRows.map(getRowId);
      const allSelected =
        ids.length > 0 && ids.every((id) => selectedIds.has(id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          ids.forEach((id) => next.delete(id));
        } else {
          ids.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [selectedIds, getRowId],
  );

  const isPageAllSelected = useCallback(
    (pageRows) =>
      pageRows.length > 0 &&
      pageRows.every((row) => selectedIds.has(getRowId(row))),
    [selectedIds, getRowId],
  );

  const isPageSomeSelected = useCallback(
    (pageRows) => pageRows.some((row) => selectedIds.has(getRowId(row))),
    [selectedIds, getRowId],
  );

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(allRows.map(getRowId)));
  }, [allRows, getRowId]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // 삭제 등으로 allRows가 바뀌면 더 이상 존재하지 않는 id는 자동으로 제외된다.
  const selectedRows = useMemo(
    () => allRows.filter((row) => selectedIds.has(getRowId(row))),
    [allRows, selectedIds, getRowId],
  );

  const isAllSelected =
    allRows.length > 0 && selectedRows.length === allRows.length;

  return {
    selectedIds,
    selectedRows,
    count: selectedRows.length,
    isSelected,
    toggleRow,
    togglePage,
    isPageAllSelected,
    isPageSomeSelected,
    isAllSelected,
    selectAll,
    clear,
  };
}

export default useTableSelection;
